// ForLearners.js
import React, { useEffect, useRef, useState } from "react";
import SEO from "../components/seo";
import { Container } from "reactstrap";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "grommet";
import TitleBannerEnquiry from "../components/titleBanner/TitleBannerEnquiry";
import { getForLearnersPageJsonLd } from "../utils/jsonld";
import "../styles/ForLearners.css";

function useAutoHeight(isOpen) {
    const ref = useRef(null);
    const [h, setH] = useState(0);
    const [dur, setDur] = useState(360);

    const compute = () => {
        const el = ref.current;
        if (!el) return { height: 0, duration: 360 };
        const height = isOpen ? el.scrollHeight + 2 : 0;
        const duration = Math.min(720, Math.max(240, 300 + height * 0.4));
        return { height, duration };
    };

    useEffect(() => {
        const { height, duration } = compute();
        setH(height);
        setDur((d) => (d === duration ? d : duration));
    }, [isOpen]);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        let raf = 0;
        const ro = new ResizeObserver(() => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                const { height, duration } = compute();
                setH((prev) => (prev === height ? prev : height));
                setDur((d) => (d === duration ? d : duration));
            });
        });
        ro.observe(el);
        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
        };
    }, [isOpen]);

    return { ref, style: { maxHeight: h, "--dd-dur": `${dur}ms` } };
}

const DropdownSection = ({
    title,
    children,
    level = 2,
    defaultOpen = false,
    externalResetSignal,
}) => {
    const [isOpen, setIsOpen] = React.useState(!!defaultOpen);
    const [resetSeq, setResetSeq] = React.useState(0);
    const [noAnim, setNoAnim] = React.useState(false);

    const lvl = Math.min(6, Math.max(2, level));
    const HeadingTag = `h${lvl}`;
    const titleId = `dd-title-${title.replace(/\s+/g, "-").toLowerCase()}`;
    const panelId = `dd-panel-${title.replace(/\s+/g, "-").toLowerCase()}`;

    const { ref, style } = useAutoHeight(isOpen);

    useEffect(() => {
        if (externalResetSignal === undefined) return;
        setNoAnim(true);
        setIsOpen(false);
        setResetSeq((s) => s + 1);
        const t = setTimeout(() => setNoAnim(false), 60);
        return () => clearTimeout(t);
    }, [externalResetSignal]);

    const handleToggle = () => {
        setIsOpen((prev) => {
            const next = !prev;
            setResetSeq((s) => s + 1);
            return next;
        });
    };

    return (
        <section className="section" aria-labelledby={titleId}>
            <div className="header-bar">
                <span className={`left-accent ${isOpen ? "open" : ""}`} />
                <button
                    type="button"
                    className="title-button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    id={titleId}
                    onClick={handleToggle}
                    title={`Toggle ${title}`}
                >
                    <HeadingTag className="title-text">{title}</HeadingTag>
                    <span className={`chevron ${isOpen ? "open" : ""}`} aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </span>
                </button>
            </div>

            <div
                id={panelId}
                role="region"
                aria-labelledby={titleId}
                aria-hidden={!isOpen}
                className={`dropdown-surface ${isOpen ? "open" : ""} ${noAnim ? "no-anim" : ""}`}
                style={style}
            >
                <div ref={ref} className="surface-inner">
                    {React.Children.map(children, (child) =>
                        React.isValidElement(child)
                            ? React.cloneElement(child, { externalResetSignal: resetSeq })
                            : child
                    )}
                </div>
            </div>
        </section>
    );
};

const ForLearners = () => {
    return (
        <>
            <SEO
                title="BIM & CAD Training Center | Ace Industrial Academy"
                description="Ace Industrial Academy is a leading training center in Singapore for IT courses, BIM courses, 3D CAD courses, 4D courses, software courses, building information modeling courses and more."
                jsonLd={getForLearnersPageJsonLd()}
            />
            <TitleBannerEnquiry
                title="For Learners"
                description="Your guide to enrolment, fees & funding, attendance, assessments and certificates at Ace Industrial Academy."
                withOnlyDescription
                hasPlainBanner
            />

            <div className="motion-guard">
                <div className="page-bg">
                    <Container fluid className="py-5" style={{ backgroundColor: "transparent" }}>
                        <Container className="py-3">
                            <h3>Course Administrative Information</h3>
                            <p className="mt-3">
                                The Terms &amp; Conditions listed below are applicable for all Ace Industrial Academy courses. <br /><br />
                                Ace Industrial Academy reserves the right to make changes or modifications to these Terms and Conditions at
                                any time without prior notice, as it deems appropriate.<br /><br />
                                We place great importance on ensuring that learners get the full benefit from our courses here at Ace
                                Industrial Academy. Therefore, we expect learners to take responsibility for their learning journey.
                                <br /><br />
                            </p>

                            <DropdownSection title="Pre-Course Administration">
                                <DropdownSection title="General" level={3}>
                                    <p>
                                        All interested applicants must comply with the Institute for Ace Industrial Academy’s entry
                                        requirements and application procedures, which may include the submission of supporting documents,
                                        attending a pre-enrolment screening, or both. <br /><br />
                                        Workstations will be provided during the training course.
                                    </p>
                                </DropdownSection>

                                <DropdownSection title="Fees and Funding Eligibility" level={3}>
                                    <p>
                                        The indicated fee payable (inclusive of GST) will cover the training sessions and the course
                                        assessment, and it will also take into account all eligible grants/subsidies.<br /><br />
                                        Learners enrolled in WSQ courses are obliged to meet the attendance requirements and achieve the
                                        competencies. Otherwise, Ace Industrial Academy reserves the right to recover the course
                                        grants/subsidies disbursed.<br /><br />
                                        Please click{" "}
                                        <a href="https://www.aia.edu.sg/funding" target="_blank" rel="noopener">here</a>{" "}
                                        for more information on available training grants. To check further on your eligibility for course fee
                                        funding, please approach the Ace Industrial Academy Pte Ltd training department{" "}
                                        <a href="mailto:aia@training.edu.sg">aia@training.edu.sg</a><br /><br />
                                    </p>
                                </DropdownSection>

                                <DropdownSection title="Registration and Payment" level={3}>
                                    <p>
                                        <strong>1. GST</strong><br /><br />
                                        If the subsidy/grant is awarded to the trainee to help defray the cost of training fees, the GST
                                        registered training provider should charge and account for GST on the gross training fees before
                                        deducting the subsidies. This is notwithstanding that the subsidies/grants may be credited to the
                                        training provider directly. To find out more,
                                        <a
                                            href="https://www.iras.gov.sg/taxes/goods-services-tax-(gst)/charging-gst-(output-tax)/common-scenarios---do-i-charge-gst/provision-of-subsidized-services"
                                            target="_blank"
                                            rel="noopener"
                                        > IRAS | Provision of subsidized services</a><br /><br />
                                        <div className="trainingContent">
                                            <strong>2. Self-Sponsored / Company-Sponsored Individuals</strong>
                                            <ul style={{ paddingLeft: "20px" }}>
                                                <br />
                                                <li>✓ Complete the course fee payment via Paynow and/or SkillsFuture Credit (SFC) / PSEA upon tax invoice provided.</li>
                                                <li>✓ Payment must be made prior to the course commencement date.</li>
                                                <li>✓ Individuals may seek reimbursement from their company.</li>
                                            </ul>
                                        </div>
                                    </p>
                                </DropdownSection>

                                <DropdownSection title="Withdrawal / Deferment and Refund" level={3}>
                                    <div className="table-wrap">
                                        <Table className="styled-table">
                                            <colgroup>
                                                <col style={{ width: "60%" }} />
                                                <col style={{ width: "40%" }} />
                                            </colgroup>

                                            <TableHeader>
                                                <TableRow>
                                                    <TableCell scope="col" className="styled-th">
                                                        Cancellation/Withdrawal Notice
                                                    </TableCell>
                                                    <TableCell scope="col" className="styled-th">
                                                        Refund Policy
                                                    </TableCell>
                                                </TableRow>
                                            </TableHeader>
                                            {/* ...rest unchanged */}

                                            <TableBody>
                                                <TableRow className="tr">
                                                    <TableCell className="td" align="center" data-label="Cancellation/Withdrawal Notice">
                                                        More than 10 working days before course commencement date
                                                    </TableCell>
                                                    <TableCell className="td" align="center" data-label="Refund Policy">
                                                        Full refund of course fees paid
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow className="tr">
                                                    <TableCell className="td" align="center" data-label="Cancellation/Withdrawal Notice">
                                                        4–10 working days before course commencement date
                                                    </TableCell>
                                                    <TableCell className="td" align="center" data-label="Refund Policy">
                                                        Refund of course fees paid with an admin charge of $100
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow className="tr">
                                                    <TableCell className="td" align="center" data-label="Cancellation/Withdrawal Notice">
                                                        3 or fewer working days before course commencement date
                                                    </TableCell>
                                                    <TableCell className="td" align="center" data-label="Refund Policy">
                                                        No refund
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </div>
                                    <p style={{ marginTop: 12 }}>
                                        <em>Please communicate your withdrawal / deferment request to <a href="mailto:training@aia.edu.sg">training@aia.edu.sg</a></em>
                                    </p>
                                </DropdownSection>

                                <DropdownSection title="Attendance" level={3}>
                                    <div className="trainingContent">
                                        <p>
                                            All Singaporean Citizens/Permanent Residents attending SkillsFuture Singapore (SSG)'s funded courses
                                            are required to register, set up their Singpass account, and download the latest version of the
                                            Singpass App prior to course commencement.<br /><br />
                                            Learners without Singpass should register for a Singpass account immediately. For more information on
                                            setting up and accessing your Singpass account, please visit the Singpass website here.<br /><br />
                                        </p>
                                        <ul>
                                            <li>
                                                Learners should be present in class (classroom and online) 15 minutes before the scheduled lesson time on the actual day and have the following ready:
                                                <ul style={{ paddingLeft: "20px" }}>
                                                    <br />
                                                    <li>1. A smart device (e.g., smartphone or iPad) with a functioning camera.</li>
                                                    <li>2. The Singpass mobile app installed on the smart device. (Note: Please log out from the other devices if your Singpass app is installed on multiple devices. Users can only utilize the Singpass mobile app from one device at a time.)</li>
                                                    <li>3. Login credentials for the Singpass App (i.e., username and password, facial recognition, or fingerprint scanning).</li>
                                                    <li>4. We strongly encourage full attendance for all classes, and SSG-funded courses require a minimum attendance of 75%.</li>
                                                </ul>
                                            </li>
                                        </ul>
                                    </div>
                                </DropdownSection>

                                <DropdownSection title="Class Cancellation" level={3}>
                                    <p>
                                        Ace Industrial Academy reserves the right to cancel, postpone or make changes to any courses should
                                        unforeseen circumstances occur. Learners will be duly notified, and the full fees will be refunded,
                                        where applicable.
                                    </p>
                                </DropdownSection>

                                <DropdownSection title="Personal Data Protection" level={3}>
                                    <p>
                                        Please refer to the Privacy Policy to understand the purposes for which Ace Industrial Academy collects,
                                        uses and discloses your Personal Data.
                                    </p>
                                </DropdownSection>
                            </DropdownSection>

                            <DropdownSection title="In-Course Administration">
                                <DropdownSection title="Re-Assessment" level={3}>
                                    <p>
                                        Learners accorded with the 'Not-Yet-Competent' (NYC) outcome will be contacted by Ace Industrial
                                        Academy’s training coordinator to be scheduled a re-assessment.<br /><br />
                                        Failing the re-assessment attempt, Ace Industrial Academy reserves the right to recover the course
                                        grants/subsidies disbursed.
                                    </p>
                                </DropdownSection>

                                <DropdownSection title="Learner Feedback" level={3}>
                                    <p>
                                        At Ace Industrial Academy, we are committed to creating a positive learning environment that encourages
                                        active learning.<br /><br />
                                        Trainers will flash a QR code at the end of the training course for feedback.<br /><br />
                                        Serious misconduct allegations against a Trainer/Assessor/Coach or staff warrant an investigation that
                                        may lead to the appropriate counselling and/or disciplinary action against the accused.
                                    </p>
                                </DropdownSection>

                                <DropdownSection title="Institute Public Disengagement Policy" level={3}>
                                    <p>
                                        At Ace Industrial Academy, we strive to provide exceptional service and support to ensure your learning
                                        experience is enriching and rewarding.<br /><br />
                                        We expect all learners to treat our staff members with respect, courtesy, and professionalism. We
                                        understand that you may have questions or concerns or require assistance, and we encourage open and
                                        constructive communication. However, abusive behavior will not be tolerated under any circumstances.<br /><br />
                                        We appreciate your understanding and cooperation in helping us maintain a positive and respectful
                                        environment at Ace Industrial Academy.
                                    </p>
                                </DropdownSection>
                            </DropdownSection>

                            <DropdownSection title="Post-Course Administration">
                                <DropdownSection title="Statement of Attainment" level={3}>
                                    <p>
                                        All WSQ certificates are issued by SkillsFuture Singapore (SSG) through MyskillsFuture Portal. Your
                                        SOAs, transcript or full qualification will be available 4 to 6 weeks upon the completion of the module
                                        or final assessment.
                                    </p>
                                </DropdownSection>
                            </DropdownSection>

                            <Container className="pt-4 px-0">
                                <h3>Technical Support</h3>
                                <p style={{ marginLeft: "0px" }}>
                                    <em>If you encounter registration issues, you can contact our training coordinator at <a href="mailto:training@aia.edu.sg">training@aia.edu.sg</a></em>
                                    <br /><br />
                                </p>
                            </Container>
                        </Container>
                    </Container>
                </div>
            </div>
        </>
    );
};
export default ForLearners;
